import {
    OreButton,
    OreCardContainer,
    OreStack,
    OreText,
    SvgPhone,
    SvgSend,
    SvgArrowRight,
    OrePill,
} from '@edenredespana/oreneta'
import { useState } from 'react'
import { TicketHistoryItem, TicketStatus } from '@/HelpCenter/domain'
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { navigationRoutes } from '@/Shared/ui/routers'

interface TicketCardProps {
    ticket: TicketHistoryItem
}

export const TicketCard = ({ ticket }: TicketCardProps): JSX.Element => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [isHovered, setIsHovered] = useState(false)

    const formattedDate = new Date(ticket.createdDate).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })

    const getStatusTone = (status: string): 'success-600' | 'warning-600' | 'grey' => {
        switch (status) {
            case TicketStatus.Resolved:
                return 'success-600'
            case TicketStatus.InProgress:
                return 'warning-600'
            default:
                return 'grey'
        }
    }
    const getStatusTextColor = (status: string): 'success-600' | 'warning-600' | 'neutral-500' => {
        switch (status) {
            case TicketStatus.Resolved:
                return 'success-600' 
            case TicketStatus.InProgress:
                return 'warning-600'
            default:
                return 'neutral-500'
        }
    }


    const handleClick = (): void => {
        navigate(navigationRoutes.ticketDetail.replace(':ticketId', String(ticket.caseId)))
    }

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ cursor: 'pointer' }}>
            <OreCardContainer>
                <OreStack
                    direction="column"
                    placeContent='stretch'
                    style={{
                        padding: '1rem',
                        backgroundColor: isHovered ? '#F8FAFC' : undefined,
                    }}>
                    <OreStack direction="row" placeContent="space-between" placeItems="start">
                        <OreStack direction="column" space="xsmall">
                            <OreText size="text-lg" tone="neutral-800" bold>
                                <Trans>{t(HelpCenterTranslations.myTickets.ticketNumber, { caseNumber: ticket.caseNumber })}</Trans>
                            </OreText>
                            <OreText size="text-md" tone="neutral-800">{ticket.caseReason}</OreText>
                            <OreText size="text-sm" tone="neutral-500">
                                {ticket.subject
                                    ? ticket.subject.length > 100
                                        ? `${ticket.subject.substring(0, 100)}...`
                                        : ticket.subject
                                    : ticket.caseReasonDetail}
                            </OreText>
                        </OreStack>
                        <OreStack direction="column" placeItems="end" space="xsmall">
                            <OreText size="text-sm" tone="neutral-400">{formattedDate}</OreText>
                            <OrePill tone="grey">
                                <OreStack direction="row" space="xsmall" placeItems="center">
                                    {ticket.caseSource === 'Web' ?
                                        <SvgSend style={{ color: '#6C7786', width: '16px', height: '16px' }} /> :
                                        <SvgPhone style={{ color: '#6C7786', width: '16px', height: '16px' }} />
                                    }
                                    <OreText size="text-sm" tone="neutral-500">{ticket.caseSource}</OreText>
                                </OreStack>
                            </OrePill>
                        </OreStack>
                    </OreStack>
                    <OreStack direction="row" placeContent="space-between" placeItems="center">
                        {ticket.status && (
                            <OrePill tone={getStatusTone(ticket.status)}>
                                <OreText size="text-sm" tone={getStatusTextColor(ticket.status)}>{ticket.status}</OreText>
                            </OrePill>
                        )}
                        <OreButton category='tertiary'>
                            <SvgArrowRight />
                        </OreButton>
                    </OreStack>
                </OreStack>
            </OreCardContainer>
        </div>
    )
}
